import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { first } from 'rxjs/operators';
import { AppStateService } from '../../services/app-state.service';
import { OrderService } from '../../services/order.service';
import { TranslateService } from '../../services/translate.service';
import { PrebookContext } from '../../models/Order';
import { PlainButtonComponent } from '../buttons/plain-button.component';
import { BottomSheetService } from '../../services/bottom-sheet.service';

/**
 * Entry route for prebook links: /:venue_id/b?d=<payload>&s=<signature>.
 * Claims the prebooking server-side (binding this device's chat to the shared
 * draft order via the chat pointer). The claim response always carries the prebook
 * context of the clicked link, and this page decides from it where the guest lands:
 * booked -> booking view, editable -> menu, otherwise a landing page explaining why
 * the pre-order cannot be made (too late, not yet open, cancelled). The guest stays
 * their own anonymous Firebase user; the signed link is the authorization.
 */
@Component({
  standalone: true,
  selector: 'app-prebook-entry',
  imports: [CommonModule, PlainButtonComponent],
  template: `
    <div class="max-w-lg mx-auto min-h-[calc(100vh)] pt-32 px-8 text-theme-text text-center">
      @if (state === 'loading') {
        <i class="fas fa-circle-notch fa-spin text-3xl text-theme-primary"></i>
        <div class="mt-4">{{getString("Öppnar din bokning...")}}</div>
      }
      @if (state === 'error') {
        <i class="fas fa-triangle-exclamation text-3xl text-theme-notification2"></i>
        <div class="mt-4 font-bold">{{getString("Länken kunde inte öppnas")}}</div>
        <div class="mt-2 text-theme-text2">{{getString("Länken är ogiltig eller har slutat gälla. Kontakta restaurangen om problemet kvarstår.")}}</div>
      }
      @if (state === 'locked' && prebook) {
        <i class="fas fa-clock text-3xl text-theme-notification2"></i>
        <div class="mt-4 font-bold">{{getString("Tiden för förbeställning har gått ut")}}</div>
        <div class="mt-2 text-theme-text2">
          {{getString("Sista tid för att genomföra förbeställningen till bokning")}} #{{prebook.prebooking.external_ref}}
          {{getString("var")}} {{formatUtc(prebook.edit_lock_at)}}.
        </div>
        <div class="mt-4 text-theme-text2">{{bookingSummary}}</div>
        <div class="mt-2 text-theme-text2">{{getString("Ni är fortfarande välkomna och beställer i stället på plats.")}}</div>
      }
      @if (state === 'not_yet_open' && prebook) {
        <i class="fas fa-hourglass-half text-3xl text-theme-primary"></i>
        <div class="mt-4 font-bold">{{getString("Förbeställningen har inte öppnat än")}}</div>
        <div class="mt-2 text-theme-text2">
          {{getString("Förbeställning till bokning")}} #{{prebook.prebooking.external_ref}}
          {{getString("öppnar")}} {{formatUtc(prebook.earliest_open_at!)}}.
        </div>
        <div class="mt-4 text-theme-text2">{{bookingSummary}}</div>
      }
      @if (state === 'cancelled' && prebook) {
        <i class="fas fa-calendar-xmark text-3xl text-theme-notification2"></i>
        <div class="mt-4 font-bold">{{getString("Bokningen är avbokad")}}</div>
        <div class="mt-2 text-theme-text2">
          {{getString("Bokning")}} #{{prebook.prebooking.external_ref}} {{getString("är avbokad och kan inte förbeställas.")}}
          {{getString("Kontakta restaurangen om detta inte stämmer.")}}
        </div>
      }
      @if (state === 'locked' || state === 'not_yet_open' || state === 'cancelled') {
        <div class="mt-8">
          <app-plain-button [text]="getString('Till startsidan')" bgColor="bg-theme-primary" color="text-theme-contrast" [customClass]="'h-14'"
                            (onClick)="navStart()"></app-plain-button>
        </div>
      }
    </div>
  `
})
export class PrebookEntryComponent implements OnInit {
  state: 'loading' | 'error' | 'locked' | 'not_yet_open' | 'cancelled' = 'loading';
  prebook?: PrebookContext;
  bookingSummary = "";

  constructor(private route: ActivatedRoute, private router: Router, private appState: AppStateService,
              private orderService: OrderService, private translate: TranslateService,
              private sheetService: BottomSheetService) { }

  ngOnInit(): void {
    combineLatest([
      this.route.queryParamMap,
      this.appState.observeAppState()
    ]).pipe(first()).subscribe(([params]) => {
      const d = params.get("d");
      const s = params.get("s");
      if (!d || !s) {
        this.state = 'error';
        return;
      }
      this.orderService.prebookClaim(d, s).subscribe({
        next: (order) => this.land(order?.prebook),
        error: (e) => {
          console.error("prebook_claim failed", e);
          this.state = 'error';
        }
      });
    });
  }

  // The server always answers a valid link with the prebook context; the data decides the view
  private land(prebook?: PrebookContext) {
    if (!prebook) {
      this.state = 'error';
      return;
    }
    this.prebook = prebook;
    this.bookingSummary = this.buildBookingSummary(prebook);
    const bookingRef = prebook.booked?.booking_ref;
    if (bookingRef) {
      // Completed booking (paid or open): the booking view, regardless of timing
      this.router.navigate(["..", "booking", bookingRef], { relativeTo: this.route, replaceUrl: true });
      return;
    }
    if (prebook.can_edit) {
      // The booking type's first guest menu, or the whole menu when it has none
      const pageId = prebook.template.menus?.[0]?.id ?? "food";
      // Sheets close on navigation: the offer opens once on the menu
      this.router.navigate(["..", "menu", pageId], { relativeTo: this.route, replaceUrl: true })
        .then(() => this.offerSharing(prebook));
      return;
    }
    switch (prebook.lock_reason) {
      case 'cancelled':
        this.state = 'cancelled';
        break;
      case 'not_yet_open':
        this.state = 'not_yet_open';
        break;
      default:
        // 'locked', or 'booked' without a booking ref (should not happen): the pre-order
        // window is over either way
        this.state = 'locked';
    }
  }

  // The booker's first visit to a prebooking the party can share: how they want to order, once per browser
  private offerSharing(prebook: PrebookContext) {
    if (prebook.role !== 'booker' || !prebook.can_invite || prebook.invite_url || prebook.party) {
      return;
    }
    const seenKey = "prebook_intro_" + prebook.prebooking.key;
    try {
      if (localStorage.getItem(seenKey)) {
        return;
      }
      localStorage.setItem(seenKey, "1");
    } catch (e) {
      // No storage: offer it every visit
    }
    this.sheetService.displaySheetClass("prebook-invite", this.getString("Hur vill ni beställa?"));
  }

  private buildBookingSummary(prebook: PrebookContext): string {
    const parts: string[] = [];
    const timeLocal = prebook.booking.time_local;
    if (timeLocal) {
      // "2026-08-15T18:00" -> "2026-08-15 18:00"
      parts.push(this.getString("Bokningstid") + " " + timeLocal.substring(0, 16).replace("T", " "));
    }
    if (prebook.booking.pax) {
      parts.push(prebook.booking.pax + " " + this.getString("gäster"));
    }
    return parts.join(" · ");
  }

  // Server sends naive UTC ISO; render as local "YYYY-MM-DD HH:MM"
  formatUtc(naiveUtcIso: string): string {
    const at = new Date(naiveUtcIso + "Z");
    const pad = (n: number) => String(n).padStart(2, "0");
    const date = at.getFullYear() + "-" + pad(at.getMonth() + 1) + "-" + pad(at.getDate());
    return date + " " + pad(at.getHours()) + ":" + pad(at.getMinutes());
  }

  navStart() {
    this.router.navigate(["..", "start"], { relativeTo: this.route });
  }

  getString(s: string): string {
    return this.translate.getString(s);
  }
}
